export default class TocScroller {
    constructor(selector) {
        this.selector = selector;

        if (document.querySelector(this.selector)) {
            this.container = document.querySelector(this.selector);
            this.tocLinks = this.container.querySelectorAll('[data-toc-link]');
            this.tocItems = this.container.querySelectorAll('.toc-item');

            this.headers = Array.from(this.tocLinks).map((link) => {
                const anchor = link.href.split('#')[1];

                return anchor ? document.getElementById(anchor) : null;
            }).filter(Boolean);

            this.ticking = false;

            window.addEventListener('scroll', (e) => {
                this.onScroll();
            });

            this.onScroll();
        }
    }

    onScroll() {
        if (!this.ticking) {
            requestAnimationFrame(this.update.bind(this));
            this.ticking = true;
        }
    }

    update() {
        let activeIndex = -1;

        for (let i = 0; i < this.headers.length; i++) {
            const header = this.headers[i];
            const isAtHeader = header.getBoundingClientRect().top > 180;

            if (isAtHeader) {
                // Always refer to the previous header
                activeIndex = i - 1;
                break;
            }

            if (!isAtHeader && (i === (this.headers.length - 1))) {
                // Check if we've scrolled past the last header
                activeIndex = this.headers.length - 1;
            }
        }

        if (activeIndex < 0 && this.headers.length) {
            activeIndex = 0;
        }

        const active = this.headers[activeIndex];

        if (active !== this.activeHeader) {
            this.activeHeader = active;

            this.tocItems.forEach((item) => {
                item.removeAttribute('data-active');
                item.removeAttribute('data-active-deepest');

                if (Number(item.dataset.depth || 0) > 0) {
                    item.classList.remove('border-primary-500');
                    item.classList.remove('hover:border-primary-500');
                    item.classList.add('border-slate-950/5', 'hover:border-slate-950/20');
                }
            });

            this.tocLinks.forEach((link) => {
                const depth = Number(link.closest('.toc-item')?.dataset.depth || 0);

                link.classList.remove(
                    'text-primary',
                    'text-primary-500',
                    'text-docs-primary',
                    'border-primary-500',
                    'border-docs-primary',
                    'font-medium',
                );

                if (depth === 0) {
                    link.classList.add('text-gray-600', 'border-slate-950/5', 'font-medium');
                    link.classList.remove('hover:border-primary-500');
                    link.classList.add('hover:border-slate-950/20', 'hover:text-gray-900');
                } else {
                    link.classList.add('text-gray-500');
                    link.classList.add('hover:text-gray-900');
                }
            });

            // It's valid for the active header to be null, when none should be selected
            if (active) {
                const activeIndexes = this.getActiveTrail(activeIndex);

                activeIndexes.forEach((index) => {
                    const item = this.tocItems[index];
                    const link = this.tocLinks[index];
                    const depth = Number(item.dataset.depth || 0);

                    item.setAttribute('data-active', 'true');
                    if (depth === 0) {
                        link.classList.remove('hover:text-gray-900');
                        link.classList.add('text-primary-500', 'font-medium');
                    } else {
                        link.classList.remove('hover:text-gray-900');
                        link.classList.add('text-primary-500');
                    }
                    link.classList.remove('text-gray-600', 'text-gray-500');

                    if (depth === 0) {
                        link.classList.remove('border-slate-950/5');
                        link.classList.remove('hover:border-slate-950/20');
                        link.classList.add('border-primary-500', 'hover:border-primary-500', 'hover:text-primary-500');
                    } else {
                        item.classList.remove('border-slate-950/5');
                        item.classList.remove('hover:border-slate-950/20');
                        item.classList.add('border-primary-500', 'hover:border-primary-500');
                        link.classList.add('hover:text-primary-500');
                    }
                });

                const deepestItem = this.tocItems[activeIndex];

                if (deepestItem) {
                    deepestItem.setAttribute('data-active-deepest', 'true');
                }
            }
        }

        this.ticking = false;
    }

    getActiveTrail(activeIndex) {
        if (activeIndex < 0) {
            return [];
        }

        const trail = [activeIndex];
        let currentDepth = Number(this.tocItems[activeIndex]?.dataset.depth || 0);

        for (let index = activeIndex - 1; index >= 0; index--) {
            const itemDepth = Number(this.tocItems[index]?.dataset.depth || 0);

            if (itemDepth < currentDepth) {
                trail.unshift(index);
                currentDepth = itemDepth;
            }

            if (currentDepth === 0) {
                break;
            }
        }

        return trail;
    }
}
